import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Dialog, Typography } from "@mui/material";
import tokens from "src/shared/theme/tokens";
import { lngs } from "../../OtherUtilities/LanguageSwitcher/LanguageSwitcher";

export interface LanguageModalProps {
  open: boolean;
  /** Called after the applicant picks a language or dismisses the modal. */
  onClose: () => void;
}

/**
 * First-visit language chooser for the unauthenticated wizard flows.
 *
 * Spanish-speaking applicants were missing the small header switcher in the
 * ui-v2 theme (marooned far top-right). This surfaces the choice up front on
 * the first visit. Intentionally bilingual — the applicant has not chosen a
 * language yet, so both options are shown in their own tongue.
 *
 * Presentation-only: it changes the i18n language and reports close. The
 * caller owns the "show once" persistence.
 */
export const LanguageModal = ({ open, onClose }: LanguageModalProps) => {
  const { i18n } = useTranslation();

  const handleSelect = (lng: string) => {
    i18n.changeLanguage(lng);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${tokens.radius.lg}px`,
          m: 2,
          width: "calc(100% - 32px)",
        },
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: tokens.fonts.display,
            fontSize: 20,
            fontWeight: 700,
            color: tokens.colors.primaryDeep,
            textAlign: "center",
            lineHeight: 1.3,
          }}
        >
          Choose your language
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.fonts.body,
            fontSize: 14,
            color: tokens.colors.inkSecondary,
            textAlign: "center",
            mb: 3,
          }}
        >
          Seleccione su idioma
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          {Object.keys(lngs).map((lng) => {
            const isActive = i18n.resolvedLanguage === lng;
            return (
              <Box
                key={lng}
                role="button"
                tabIndex={0}
                aria-label={lngs[lng].nativeName}
                onClick={() => handleSelect(lng)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(lng);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                  px: 2,
                  py: 3,
                  borderRadius: `${tokens.radius.md}px`,
                  border: `1.5px solid ${
                    isActive ? tokens.colors.primary : tokens.colors.border
                  }`,
                  backgroundColor: isActive
                    ? tokens.colors.primarySoft
                    : tokens.colors.surface,
                  transition:
                    "border-color 0.15s ease, background-color 0.15s ease",
                  "&:hover": {
                    borderColor: tokens.colors.primary,
                    backgroundColor: tokens.colors.hover,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${tokens.colors.primary}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: tokens.fonts.display,
                    fontSize: 16,
                    fontWeight: 600,
                    color: tokens.colors.ink,
                  }}
                >
                  {lngs[lng].nativeName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: tokens.fonts.body,
                    fontSize: 12,
                    color: tokens.colors.inkMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    mt: 0.5,
                  }}
                >
                  {lng}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};
